import React, { useState, useEffect } from 'react';
import PricingCard from '../components/PricingCard'; 
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, collection, addDoc, getCountFromServer, query, where } from 'firebase/firestore';
import { db } from '../firebase'; 
import Spinner from '../components/Spinner'; 
import Header from '../components/Header'; 
import Footer from '../components/Footer'; 

const MembershipPage = () => {
  const [userRole, setUserRole] = useState(null); 
  const [userPlan, setUserPlan] = useState(null); 
  const [trialEndDate, setTrialEndDate] = useState(null); 
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState({ show: false, message: '', isSuccess: true });
  const [currentlySubscribing, setCurrentlySubscribing] = useState(null);
  const [isFreeTrialAvailable, setIsFreeTrialAvailable] = useState(true);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const MAX_FREE_TRIAL_USERS = 100;

  useEffect(() => {
    const checkFreeTrialStatus = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('subscriptionType', '==', 'free_trial'));
        const snapshot = await getCountFromServer(q);
        const count = snapshot.data().count;
        setTotalSubscribers(count);
        setIsFreeTrialAvailable(count < MAX_FREE_TRIAL_USERS);
      } catch (error) {
        console.error('Error checking free trial status:', error);
        // Assume free trial is available if there's an error checking
        setIsFreeTrialAvailable(true);
      }
    };
    
    checkFreeTrialStatus();
  }, []);

  const handleSubscribe = async (planName) => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      setError('Please sign in to subscribe');
      return;
    }

    setCurrentlySubscribing(planName);
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      // Check if user already has a plan
      if (userDoc.exists() && userDoc.data().plan) {
        setSubscriptionStatus({
          show: true,
          message: 'You already have an active subscription.',
          isSuccess: false
        });
        return;
      }

      // Check if free trial is still available
      if (totalSubscribers >= MAX_FREE_TRIAL_USERS) {
        setSubscriptionStatus({
          show: true,
          message: 'Sorry, the free trial period has ended. Please contact support for subscription options.',
          isSuccess: false
        });
        return;
      }

      // For now, just create a free trial without payment
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + 90); // 90 days trial
      
      const userUpdate = {
        plan: 'free_trial',
        trialStartDate: serverTimestamp(),
        trialEndDate: trialEndDate,
        updatedAt: serverTimestamp(),
        subscriptionStatus: 'active',
        subscriptionType: 'free_trial',
        features: ['all'] // Grant all features for trial users
      };

      // Update user document
      await setDoc(userRef, userUpdate, { merge: true });
      
      // Update local state
      setUserPlan('free_trial');
      setTrialDaysLeft(90);
      
      // Update subscriber count
      setTotalSubscribers(prev => prev + 1);
      if (totalSubscribers + 1 >= MAX_FREE_TRIAL_USERS) {
        setIsFreeTrialAvailable(false);
      }
      
      setSubscriptionStatus({
        show: true,
        message: `🎉 Congratulations! You've activated your 90-day free trial. Enjoy full access to all features!`,
        isSuccess: true
      });
    } catch (error) {
      console.error('Error subscribing:', error);
      setSubscriptionStatus({
        show: true,
        message: error.code === 'permission-denied' 
          ? 'You do not have permission to perform this action. Please contact support.'
          : 'Failed to process subscription. Please try again later.',
        isSuccess: false
      });
    } finally {
      setCurrentlySubscribing(null);
    }
  };
  
  const closeSubscriptionStatus = () => {
    setSubscriptionStatus({ ...subscriptionStatus, show: false });
  };

  useEffect(() => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (currentUser) {
      const userDocRef = doc(db, 'users', currentUser.uid);
      getDoc(userDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            const userData = docSnap.data();
            setUserRole(userData.role || 'parent'); 
            setUserPlan(userData.plan || (userData.role === 'parent' ? 'parent_basic' : 'trial')); 
            
            if (userData.trialEnd && userData.trialEnd.toDate) { 
              const endDate = userData.trialEnd.toDate();
              setTrialEndDate(endDate);
              const today = new Date();
              const diffTime = endDate - today; 
              if (diffTime > 0) {
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                setTrialDaysLeft(diffDays);
              } else {
                setTrialDaysLeft(0); 
              }
            }
          } else {
            setError('User data not found.');
            setUserRole('parent'); 
            setUserPlan('parent_basic');
          }
        })
        .catch((err) => {
          console.error("Error fetching user data:", err);
          setError('Failed to load user data.');
          setUserRole('parent'); 
          setUserPlan('parent_basic');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setUserRole('parent'); 
      setUserPlan('parent_basic');
      setLoading(false);
    }
  }, []);

  const renderPricingPlans = () => {
    switch (userRole) {
      case 'parent':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="relative">
              {!isFreeTrialAvailable && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  Limited Time
                </div>
              )}
              <PricingCard
                planName="Free Trial"
                priceMonthly="₹0"
                priceQuarterly={null}
                features={[
                  'Full access to all features',
                  'No credit card required',
                  'Cancel anytime',
                  '90 days of premium access',
                  'Priority support'
                ]}
                ctaText={userPlan ? "Current Plan" : "Start Free Trial"} 
                isCurrentPlan={!!userPlan}
                onSubscribe={() => handleSubscribe('Free Trial')}
                loading={!!currentlySubscribing}
                disabled={!isFreeTrialAvailable}
              />
            </div>

          </div>
        );
      case 'therapist': 
      case 'professional': 
        return (
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <PricingCard
              planName="Pro Lite"
              priceMonthly="₹999"
              priceQuarterly="₹2599 (Save ₹398)"
              features={[
                'Unlock parent contact',
                'Access job details',
                'Standard profile listing'
              ]}
              ctaText={userPlan === 'pro_lite' ? "Current Plan" : "Subscribe Now"}
              trialInfo="Free till June 30"
              isCurrentPlan={userPlan === 'pro_lite'}
              onSubscribe={() => handleSubscribe('Pro Lite')}
              loading={currentlySubscribing === 'Pro Lite'}
            />
            <PricingCard
              planName="Pro Premium"
              priceMonthly="₹1499"
              priceQuarterly="₹3999 (Save ₹498)"
              features={[
                'All Pro Lite features',
                'Priority ranking in searches',
                'Enhanced profile visibility',
                'Direct messaging with parents'
              ]}
              ctaText={userPlan === 'pro_premium' ? "Current Plan" : "Subscribe Now"}
              isCurrentPlan={userPlan === 'pro_premium'}
              onSubscribe={() => handleSubscribe('Pro Premium')}
              loading={currentlySubscribing === 'Pro Premium'}
            />
          </div>
        );
      case 'cdc':
      case 'inclusive_school': 
      case 'mainstream_school':
      case 'corporate':
        const orgRoleName = userRole.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '); 
        return (
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <PricingCard
              planName={`${orgRoleName} Basic`}
              priceMonthly="₹1999"
              priceQuarterly="₹5299 (Save ₹698)"
              features={[
                'Basic organization profile',
                'Job posting (up to 3 active)',
                'Standard search visibility',
                'Email support'
              ]}
              ctaText={userPlan === 'org_basic' ? "Current Plan" : "Subscribe Now"}
              isCurrentPlan={userPlan === 'org_basic'}
              onSubscribe={() => handleSubscribe(`${orgRoleName} Basic`)}
              loading={currentlySubscribing === `${orgRoleName} Basic`}
            />
            <PricingCard
              planName={`${orgRoleName} Premium`}
              priceMonthly="₹3499"
              priceQuarterly="₹9299 (Save ₹1198)"
              features={[
                'All Basic features',
                'Unlimited job postings',
                'Priority search ranking',
                'Dedicated account manager',
                'Advanced analytics'
              ]}
              ctaText={userPlan === 'org_premium' ? "Current Plan" : "Subscribe Now"}
              isCurrentPlan={userPlan === 'org_premium'}
              onSubscribe={() => handleSubscribe(`${orgRoleName} Premium`)}
              loading={currentlySubscribing === `${orgRoleName} Premium`}
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="text-center text-red-500 py-10 min-h-[calc(100vh-200px)]">
          Error: {error}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Start Your Free Trial</h1>
          <p className="text-xl text-gray-600 mb-6">Get full access to all features for 90 days, completely free!</p>
          
          {isFreeTrialAvailable ? (
            <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    Limited time offer: First {MAX_FREE_TRIAL_USERS} users get 90 days free! 
                    <span className="font-medium">{MAX_FREE_TRIAL_USERS - totalSubscribers} spots remaining</span>
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    The free trial period has ended. Please contact support for subscription options.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-blue-50 p-6 rounded-lg mb-8">
            <h2 className="text-2xl font-bold text-blue-800 mb-4">What's Included in Your Free Trial</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Full access to all features</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>No credit card required</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Cancel anytime</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>90 days of premium access</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto">
          {trialDaysLeft !== null && trialDaysLeft > 0 && (
            <div className="mb-8 p-4 bg-blue-50 text-blue-800 rounded-lg text-center">
              Your free trial ends in {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}. 
              Upgrade now to continue enjoying premium features.
            </div>
          )}
          <section>
            {renderPricingPlans()}
          </section>
        </div>

        {/* Subscription Status Modal */}
        {subscriptionStatus.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div 
                className={`text-2xl font-bold mb-4 ${
                  subscriptionStatus.isSuccess ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {subscriptionStatus.isSuccess ? 'Thank You!' : 'Error'}
              </div>
              <p className="mb-6">{subscriptionStatus.message}</p>
              <button
                onClick={closeSubscriptionStatus}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg 
                  hover:bg-indigo-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default MembershipPage;
